/**
 * ENG-10857: the Claude Code version a Docker-isolated agent ACTUALLY runs.
 *
 * ## Why this module exists
 *
 * `hosts.framework_version` is the version on the MANAGER'S OWN PATH — the
 * adapter's `getVersion()` shells out in the manager's environment and never
 * enters a container (see the comment at `manager-worker.ts` §1b). For an
 * isolated agent that number describes a binary the agent cannot reach: the
 * container runs `claude` from the `agt-runtime` image, installed at BUILD time
 * (`host-infra/agt-runtime/Dockerfile` `ARG CLAUDE_CODE_VERSION`), on its own
 * filesystem. Only `/root/.claude`, `/root/.claude.json` and
 * `/root/.augmented/_mcp` are mounted in; the binary is not among them.
 *
 * Measured on integritylabs 2026-09-21: host `2.1.251`, container `2.1.259`, on
 * the same machine, with all five agents' PIDs in `docker-<id>.scope`. So
 * `2.1.259` was what every agent ran and `2.1.251` ran nothing. ADR-0051 §step 6
 * calls for exactly this split, and has since 2026-07-25.
 *
 * ## Why not report `desired_runtime_image` instead
 *
 * It would be cheaper and it would be wrong. `desired_runtime_image` is what the
 * control plane TOLD the host to run, and the gap between that and reality is
 * wide enough to drive the original bug through:
 *
 *   - `ensureIsolationImage` is fire-and-forget (`manager-worker.ts:15717`).
 *   - It marks a URI "ensured" after GIVING UP — three failures adds the URI to
 *     the same set a success does (`manager-worker.ts:5414-5417`), so "pulled"
 *     and "gave up" are indistinguishable from outside.
 *   - `AGT_ISOLATION_IMAGE` makes it return before it even reads its argument
 *     (`manager-worker.ts:5435`), and the spawn path honours the same env.
 *   - Nothing recycles a running container when the tag moves, so an agent keeps
 *     its spawn-time binary regardless.
 *
 * Reporting an intention as though it were an observation is the defect this
 * module was written to correct. It must not be the mechanism.
 *
 * ## Why not `docker exec` on every tick
 *
 * {@link samplePidPressureFor}'s module doc rejects per-cycle `docker exec` on
 * two grounds, the second of which it calls the real one: spawning a subprocess
 * per agent per cycle to measure a resource CONSUMES that resource. That
 * reasoning applies here with an extra edge — a baked `claude --version` cannot
 * change for the life of a container, so a per-tick read re-measures a constant.
 *
 * So the exec runs ONCE PER CONTAINER, not once per sample. The cache is keyed
 * on the container's cgroup directory, obtained from the already-cached
 * {@link resolveCgroupDirReal}: that path embeds the container id, is
 * revalidated with a plain `existsSync`, and changes when the container
 * restarts. Steady state therefore costs one `existsSync` per agent per tick and
 * spawns nothing at all — the same guarantee the PID sampler makes, reached the
 * same way.
 *
 * ## Four states, because `null` cannot mean three things
 *
 * ADR-0051's originating incident was three version numbers with no legible
 * signal. A single `null` covering "not isolated", "not sampled yet" and
 * "container is wedged" would restage that inside the fix: the third is the one
 * an operator needs at 3am, and it is the one a blank hides best. Consumers get
 * an explicit {@link RuntimeVersionState} and a consecutive-failure count,
 * mirroring the threshold shape `claude-code-updater.ts` already uses for the
 * host binary.
 *
 * ABSENT (the field missing entirely) still means NO SIGNAL — a manager older
 * than this module omits it, and a consumer that read absence as "broken" would
 * paint the fleet red the moment one host lagged a release.
 */

import { execFileSync } from 'node:child_process';
import { execFile as execFileCb } from 'node:child_process';
import { promisify } from 'node:util';
import { boundedArgv, hasTimeoutBinary, DEFAULT_KILL_AFTER_MS } from './bounded-exec.js';

const execFileAsync = promisify(execFileCb);
import { resolveCgroupDirReal } from './pid-pressure-sampler.js';
import { effectiveIsolationMode } from './persistent-session.js';

/**
 * How often the sampler ticks.
 *
 * Slower than the PID sampler's 30s because this measures a CONSTANT, not a
 * level: within one container the answer cannot change, so the cadence only
 * governs how quickly a NEW container is noticed. Five minutes bounds that at
 * one interval of staleness on a value that moves when an agent respawns.
 */
export const SAMPLE_INTERVAL_MS = 5 * 60_000;

export type RuntimeVersionState =
  /** Read succeeded; `version` is what the container runs. */
  | 'ok'
  /** Not Docker-isolated — the host binary governs, and `framework_version` already reports it. */
  | 'not-isolated'
  /** Isolated, but this agent has not been sampled yet (first tick, just spawned). */
  | 'unsampled'
  /** Isolated and the read FAILED — container stopped, mid-restart, or wedged. */
  | 'error';

export interface RuntimeVersionSample {
  state: RuntimeVersionState;
  /** Non-null if and only if `state === 'ok'`. Never inferred, never defaulted. */
  version: string | null;
  /** ISO timestamp of the read that produced `version`. Non-null iff `state === 'ok'`. */
  sampledAt: string | null;
  /**
   * Consecutive failed reads for this agent. `0` unless `state === 'error'`.
   * Carried so a consumer can tell a transient miss (container restarting) from
   * a persistent one (wedged) — the "stuck vs intentionally behind" distinction
   * ADR-0051 requires and `claude-code-updater.ts` already implements for the
   * host binary.
   */
  failures: number;
}

/** Injectable I/O seam, so tests never spawn docker or touch a filesystem. */
export interface RuntimeVersionIo {
  /** Override the isolation check. */
  isolatedFor?: (codeName: string) => boolean;
  /** Resolve the container's cgroup dir; its change is our restart signal. */
  resolveCgroupDir?: (codeName: string) => string | null;
  /** Run `claude --version` inside the container; null when the call failed. */
  readVersion?: (codeName: string) => Promise<string | null> | string | null;
  now?: () => number;
}

/**
 * codeName -> the cgroup dir the cached sample was taken against, plus the
 * sample. The dir is the cache KEY in spirit: when it changes the container has
 * been replaced and the baked version may differ, so the exec must run again.
 */
const lastSample = new Map<string, { dir: string; sample: RuntimeVersionSample }>();

/** codeName -> consecutive failed reads. Cleared on any success. */
const failureCount = new Map<string, number>();

/**
 * The ONE subprocess in this module, and it is ASYNC — which is the whole point.
 *
 * `hardBoundedCapture` is `execFileSync`. Bounding it stops it running forever;
 * it does NOT stop it blocking. A sync docker call parks the event loop for the
 * full timeout plus the kill grace, and a miss happens exactly when the daemon
 * is unhealthy — so three wedged reads could stall the manager ~36s and delay
 * the ~30s liveness heartbeat. That is ENG-9781/ENG-9879's measured failure
 * mode (spawnSync docker froze scheduling for every agent on agt-aws-1), and
 * re-introducing it inside a diagnostic would be a poor trade: this module
 * exists to REPORT a version, never to cost availability to do it.
 *
 * The bound itself is unchanged — `boundedArgv` returns the same
 * `timeout --signal=TERM --kill-after=...` wrapping the sync path uses, shared
 * rather than copied so the two cannot drift. The Node-level `timeout` is kept
 * as the second, independent deadline for the case where `timeout` itself wedges.
 */
async function readVersionReal(codeName: string): Promise<string | null> {
  const timeoutMs = 10_000;
  // Probed once per process, not per read: the probe is itself a subprocess,
  // and running it on every sample would reintroduce the cost this is avoiding.
  timeoutBinaryAvailable ??= hasTimeoutBinary(execFileSync);
  const { execFile: file, execArgs } = boundedArgv(
    'docker',
    ['exec', `agt-${codeName}`, 'claude', '--version'],
    timeoutMs,
    DEFAULT_KILL_AFTER_MS,
    timeoutBinaryAvailable,
  );
  try {
    const { stdout } = await execFileAsync(file, execArgs, {
      encoding: 'utf-8',
      timeout: timeoutBinaryAvailable ? timeoutMs + DEFAULT_KILL_AFTER_MS + 5_000 : timeoutMs,
    });
    return String(stdout ?? '');
  } catch {
    // Every outcome is a value here, as in bounded-exec: a caller on the
    // manager's path must handle "no answer" explicitly rather than through a
    // catch that also swallows real answers. The caller counts this failure.
    return null;
  }
}

/** Memoised `timeout` availability. See {@link readVersionReal}. */
let timeoutBinaryAvailable: boolean | undefined;

/**
 * `claude --version` prints `2.1.259 (Claude Code)`. Take the leading
 * dotted-numeric run and nothing else.
 *
 * Returns null rather than the raw string on no match: a version field that can
 * hold arbitrary text is a field that will one day hold an error message and be
 * rendered as a version.
 */
export function parseClaudeVersion(raw: string | null): string | null {
  if (raw == null) return null;
  const m = raw.trim().match(/\b(\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?)\b/);
  return m ? m[1]! : null;
}

/** Sample one agent. Spawns a subprocess only when the container has changed. */
export async function sampleRuntimeVersionFor(
  codeName: string,
  io: RuntimeVersionIo = {},
): Promise<RuntimeVersionSample> {
  const isolated = io.isolatedFor?.(codeName) ?? effectiveIsolationMode(codeName) === 'docker';
  // The gate is first and costs nothing. An unisolated agent runs the host
  // binary, which `hosts.framework_version` already reports; measuring it again
  // here would be a second copy of one fact, free to drift from the first.
  if (!isolated) {
    lastSample.delete(codeName);
    failureCount.delete(codeName);
    return { state: 'not-isolated', version: null, sampledAt: null, failures: 0 };
  }

  const resolveDir = io.resolveCgroupDir ?? resolveCgroupDirReal;
  const readVersion = io.readVersion ?? readVersionReal;
  const now = io.now ?? Date.now;

  const dir = resolveDir(codeName);
  if (dir == null) {
    // Isolated but the container could not be resolved: stopped, mid-restart,
    // or the daemon is not answering. An ERROR, explicitly — not the same blank
    // an unisolated agent gets. `resolveCgroupDirReal` already backs off on
    // repeated failures, so this does not become a subprocess per tick.
    const failures = (failureCount.get(codeName) ?? 0) + 1;
    failureCount.set(codeName, failures);
    // Drop any cached sample: the container it described is gone, and a stuck
    // gauge that still reports a version is worse than one that admits it lost
    // the thread.
    lastSample.delete(codeName);
    return { state: 'error', version: null, sampledAt: null, failures };
  }

  const cached = lastSample.get(codeName);
  // Same container as last time ⇒ same baked binary. This is the steady-state
  // path and it spawns nothing.
  if (cached && cached.dir === dir) return cached.sample;

  const version = parseClaudeVersion(await readVersion(codeName));
  if (version == null) {
    const failures = (failureCount.get(codeName) ?? 0) + 1;
    failureCount.set(codeName, failures);
    lastSample.delete(codeName);
    return { state: 'error', version: null, sampledAt: null, failures };
  }

  failureCount.delete(codeName);
  const sample: RuntimeVersionSample = {
    state: 'ok',
    version,
    sampledAt: new Date(now()).toISOString(),
    failures: 0,
  };
  lastSample.set(codeName, { dir, sample });
  return sample;
}

/** Drive one tick across the live agents. Exported for tests and the sampler loop. */
export async function sampleRuntimeVersionOnce(
  codeNames: Iterable<string>,
  io: RuntimeVersionIo = {},
): Promise<void> {
  // SERIAL, deliberately. These reads only happen on a cache miss — a container
  // that restarted or a daemon that is not answering — so a parallel fan-out
  // would aim its worst case (every agent missing at once, i.e. a wedged
  // daemon) at the daemon that is already struggling. Serial keeps the load
  // flat; being async is what keeps it off the event loop.
  for (const codeName of codeNames) await sampleRuntimeVersionFor(codeName, io);
}

/**
 * Pure read of the last tick's result — the accessor `collectDiagnostics` is
 * handed. Never samples, so the heartbeat path stays free of subprocesses.
 *
 * An agent the sampler has not reached yet is `unsampled`, which is deliberately
 * NOT `error`: "we have not looked" and "we looked and it is broken" are
 * different answers and only one of them should wake anybody up.
 */
export function runtimeVersionFor(codeName: string): RuntimeVersionSample {
  const cached = lastSample.get(codeName);
  if (cached) return cached.sample;
  const failures = failureCount.get(codeName) ?? 0;
  if (failures > 0) return { state: 'error', version: null, sampledAt: null, failures };
  return { state: 'unsampled', version: null, sampledAt: null, failures: 0 };
}

let timer: NodeJS.Timeout | null = null;
/** Re-entrancy latch. See the skip in {@link startRuntimeVersionSampler}. */
let sampling = false;

export function startRuntimeVersionSampler(
  liveAgents: () => Iterable<string>,
  /** Test seam only — production passes nothing and the real IO is used. */
  io: RuntimeVersionIo = {},
): void {
  if (timer) return;
  timer = setInterval(() => {
    // SKIP while a run is in flight, never queue behind one.
    //
    // Going async (this PR) made overlap possible where the sync version could
    // not have it: a run slower than the 5-minute interval would otherwise have
    // a second run reading the same uncached containers. `lastSample` and
    // `failureCount` are shared mutable state, so concurrent runs can inflate a
    // failure count past the number of real failures, and a late failure from
    // run A can delete a cache entry run B just succeeded in writing — leaving
    // a healthy agent reported as `error`.
    //
    // Skipping rather than queueing is the right direction: a run this slow
    // means the daemon is struggling, and the correct response to that is LESS
    // docker load, not a backlog of it.
    if (sampling) return;
    sampling = true;
    // The catch is load-bearing, not decoration: without it a rejection here is
    // an unhandled rejection rather than the swallowed error the sync version
    // produced. `finally` releases the latch on both paths — an early return on
    // the error path would wedge the sampler permanently.
    void sampleRuntimeVersionOnce(liveAgents(), io)
      .catch(() => {})
      .finally(() => {
        sampling = false;
      });
  }, SAMPLE_INTERVAL_MS);
  timer.unref?.();
}

export function stopRuntimeVersionSampler(): void {
  if (timer) clearInterval(timer);
  timer = null;
}

export function __resetRuntimeVersionStateForTests(): void {
  sampling = false;
  stopRuntimeVersionSampler();
  lastSample.clear();
  failureCount.clear();
}
